import { useEffect, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import flagsmith from '@flagsmith/flagsmith';
import { useUser } from '@descope/react-sdk';
import { debugLog } from '@/lib/debug';
import { useAuth } from './useAuth';

/**
 * Hook to manage Flagsmith identity based on user email and organization context
 *
 * Sets identity when user is authenticated and updates when organization changes.
 * Calls flagsmith.identify() with user email as identifier and organization_id as trait.
 * Calls flagsmith.logout() when user logs out.
 */
export function useFlagsmithIdentity(): void {
  const { user: descopeUser } = useUser();
  const { user: authUser, isAuthenticated } = useAuth();
  const routeParams = useParams<{ organizationId: string }>();
  const location = useLocation();
  const lastIdentityRef = useRef<{
    userEmail: string;
    organizationId: string | undefined;
  } | null>(null);

  // Extract organizationId from route params or parse from pathname
  // Route params work when inside a Route component, but we're at the root level
  // So we need to parse the pathname as fallback
  const organizationId =
    routeParams.organizationId ||
    (() => {
      // Parse pathname to extract organizationId
      // Path structure: /:organizationId/* or /dashboard/*
      const pathSegments = location.pathname.split('/').filter(Boolean);
      // First segment is organizationId if it's not 'dashboard'
      const firstSegment = pathSegments[0];
      // Organization IDs are typically long alphanumeric strings (like T35Ne9HxIvGiPAMt79Ceg9OQqw0J)
      // Dashboard is a known route, so exclude it
      if (firstSegment && firstSegment !== 'dashboard') {
        return firstSegment;
      }
      return undefined;
    })();

  useEffect(() => {
    debugLog('[Flagsmith Identity] Effect triggered', {
      isAuthenticated,
      hasEmail: !!authUser?.email,
      organizationId,
      currentIdentity: lastIdentityRef.current,
    });

    // Only set identity if user is authenticated and has email
    if (!isAuthenticated || !authUser?.email) {
      // If user is not authenticated, logout from Flagsmith
      if (lastIdentityRef.current) {
        debugLog('[Flagsmith Identity] Logging out from Flagsmith');
        flagsmith.logout();
        lastIdentityRef.current = null;
      }
      return;
    }

    const userEmail = authUser.email;
    const orgId = organizationId || undefined;

    // Check if identity has changed
    const currentIdentity: {
      userEmail: string;
      organizationId: string | undefined;
    } = { userEmail, organizationId: orgId };
    const lastIdentity = lastIdentityRef.current;

    if (
      lastIdentity &&
      lastIdentity.userEmail === currentIdentity.userEmail &&
      lastIdentity.organizationId === currentIdentity.organizationId
    ) {
      // Identity hasn't changed, no need to update
      return;
    }

    // Always send organization_id explicitly (empty string when absent) so we
    // overwrite any previously-persisted trait on the Flagsmith identity.
    // Passing `{}` leaves prior traits intact on Flagsmith's server, which caused
    // org-specific flag values to leak into personal-account evaluations after a
    // soft navigation.
    const traits: Record<string, string> = {
      organization_id: orgId ?? '',
    };

    debugLog('[Flagsmith Identity] Setting identity:', {
      userEmail,
      organizationId: orgId,
      traits,
      previousIdentity: lastIdentity,
    });

    // Async function to handle identify call with error handling
    const setIdentity = async (): Promise<void> => {
      try {
        debugLog('[Flagsmith Identity] Calling flagsmith.identify...', {
          userEmail,
          traits,
        });
        await flagsmith.identify(userEmail, traits);
        debugLog('[Flagsmith Identity] Identity set successfully', {
          userEmail,
          traits,
        });

        // Check if identity was set correctly
        const flagsmithIdentity = flagsmith.identity;
        const currentTraits = flagsmith.getAllTraits();
        debugLog('[Flagsmith Identity] Current Flagsmith state:', {
          identity: flagsmithIdentity,
          traits: currentTraits,
        });

        // Only set ref after successful identification
        // This ensures retries can happen if the call fails
        lastIdentityRef.current = currentIdentity;
      } catch (error) {
        // Log error but don't set ref, allowing retry on next effect run
        console.error(
          '[Flagsmith Identity] Failed to set Flagsmith identity:',
          {
            error,
            userEmail,
            traits,
          }
        );
        // Don't set lastIdentityRef.current on failure so the effect can retry
      }
    };

    void setIdentity();
  }, [
    isAuthenticated,
    authUser?.email,
    organizationId,
    descopeUser,
    location.pathname,
  ]);
}
